import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
  Icon,
} from "@chakra-ui/react";
import {
  KYDEngageAutomationAnalytics,
  KYDEngageAutomationChoiceStep,
  KYDEngageAutomationDefinition,
  KYDEngageAutomationEmailStep,
  KYDEngageAutomationStep,
} from "@common/interfaces/KYDEngageAutomation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import actions from "src/actions";
import StatCard from "../Components/StatCard";
import AutomationsFlowGraph from "./FlowGraph";
import { IoPause, IoPlay } from "react-icons/io5";
import { PiFlowArrowLight } from "react-icons/pi";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { jsx } from "@emotion/react";

const numberFormatter = new Intl.NumberFormat();

const formatNumber = (value?: number) => numberFormatter.format(value ?? 0);
const formatRevenue = (value?: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format((value ?? 0) / 100);
const formatRate = (value?: number) => `${((value ?? 0) * 100).toFixed(1)}%`;
const GRID_BG =
  "linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px)";

const statusColorScheme = (status: KYDEngageAutomationDefinition["status"]) => {
  if (status === "ACTIVE") {
    return "green";
  }
  if (status === "PAUSED") {
    return "orange";
  }
  if (status === "ARCHIVED") {
    return "gray";
  }
  return "purple";
};

const getStepAdditionalDetails = (
  step: KYDEngageAutomationStep
): jsx.JSX.Element => {
  if (step.type === "EMAIL") {
    return (
      <Text fontSize="16px" fontWeight="500" color="gray.600">
        Subject: {(step as KYDEngageAutomationEmailStep).subject || "-"}
      </Text>
    );
  }

  const choiceStep = step as KYDEngageAutomationChoiceStep;
  const values = choiceStep.condition.validation_values?.join(", ") || "";
  const condition = choiceStep.condition;
  return (
    <Text fontSize="16px" fontWeight="500" color="gray.600">
      {`${choiceStep.condition.fact} = ${
        values ? `[${values}]` : condition.equals ? "true" : "false"
      }`}
    </Text>
  );
};

const getStepTypeLabel = (step: KYDEngageAutomationStep): jsx.JSX.Element => {
  return (
    <Text fontSize="16px" fontWeight="500" color="gray.600">
      {step.type === "EMAIL" ? "Email" : "Choice"}
    </Text>
  );
};

const formatDelayLabel = (delayMinutes?: number) => {
  const delay = Math.max(delayMinutes || 0, 0);
  if (delay === 0) {
    return "Immediately";
  }
  if (delay % (60 * 24) === 0) {
    const days = delay / (60 * 24);
    return `${days} day${days === 1 ? "" : "s"} after trigger`;
  }
  if (delay % 60 === 0) {
    const hours = delay / 60;
    return `${hours} hour${hours === 1 ? "" : "s"} after trigger`;
  }
  return `${delay} minute${delay === 1 ? "" : "s"} after trigger`;
};

const titleFromStepId = (stepId: string) =>
  stepId
    .replace(/^step_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const STEP_DISPLAY = {
  triggerEvent: {
    "checkout.completed": "Customers who have completed a checkout",
    "first.checkout.completed":
      "Customers who have completed their first checkout",
    "event.cart.expired": "Customers who have had their cart expired",
    "event.ticket.checkedin":
      "Customers who have checked in a ticket to an event",
  },
};

const deriveAudienceLabel = (automation: KYDEngageAutomationDefinition) => {
  const trigger = automation.trigger;
  if (trigger.type === "EVENT") {
    if (trigger.event_detail?.first_time_for_org) {
      return `${STEP_DISPLAY.triggerEvent[`first.${trigger.event_type}`]}`;
    }
    return STEP_DISPLAY.triggerEvent[trigger.type];
  } else {
    return `People that match the automation conditions.`;
  }
};

type StepAttributionMetrics = Partial<KYDEngageAutomationAnalytics> & {
  updated_at?: string;
};

type MetricsWithStepAttribution = KYDEngageAutomationDefinition["metrics"] & {
  attribution_by_step?: Record<
    string,
    {
      last_30_days?: StepAttributionMetrics;
      updated_at?: string;
    }
  >;
};

const AutomationsDetail = () => {
  const [automation, setAutomation] =
    useState<KYDEngageAutomationDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState<string | undefined>(
    undefined
  );
  const [showDetailsByStepId, setShowDetailsByStepId] = useState<
    Record<string, boolean>
  >({});
  const [showOperationalMetrics, setShowOperationalMetrics] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { automation_id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const fetchAutomation = useCallback(async () => {
    if (!automation_id) {
      navigate("/campaigns/automations");
      return;
    }

    setIsLoading(true);
    try {
      const response = await actions.ADMIN.fetchEngageAutomation(automation_id);
      const loadedAutomation = response?.automation || null;
      setAutomation(loadedAutomation);
      if (loadedAutomation?.steps?.[0]?.id) {
        setSelectedStepId((current) => current || loadedAutomation.steps[0].id);
      }
    } catch (error: any) {
      console.error("Failed to fetch engage automation", error);
      toast({
        title: "Automation not found",
        description: error?.message || "Please try again.",
        status: "error",
      });
      navigate("/campaigns/automations");
    } finally {
      setIsLoading(false);
    }
  }, [automation_id, navigate, toast]);

  useEffect(() => {
    fetchAutomation();
  }, [fetchAutomation]);

  const onToggleStatus = useCallback(async () => {
    if (!automation) {
      return;
    }

    const shouldActivate = automation.status !== "ACTIVE";
    const nextStatus = shouldActivate ? "ACTIVE" : "PAUSED";
    setIsUpdatingStatus(true);

    try {
      const response = await actions.ADMIN.updateEngageAutomation(
        automation.id,
        { status: nextStatus }
      );

      setAutomation(response.automation);
      toast({
        title: shouldActivate ? "Automation activated" : "Automation paused",
        status: "success",
      });
    } catch (error: any) {
      console.error("Failed to update automation status", error);
      toast({
        title: "Failed to update status",
        description: error?.message || "Please try again.",
        status: "error",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [automation, toast]);

  const selectedStep = useMemo(() => {
    if (!automation || !selectedStepId) {
      return null;
    }
    return automation.steps.find((step) => step.id === selectedStepId) || null;
  }, [automation, selectedStepId]);

  if (isLoading) {
    return (
      <VStack py={8} w="100%">
        <Spinner />
      </VStack>
    );
  }

  if (!automation) {
    return null;
  }

  const last30 = automation.metrics?.last_30_days;
  const runCounters = automation.operational_metrics?.last_30_days_runs;
  const steps = automation.steps;
  const emailSteps = steps.filter(
    (step) => step.type === "EMAIL"
  ) as KYDEngageAutomationEmailStep[];

  return (
    <VStack h="100%" gap={0} w="100%" alignItems="stretch">
      <HStack
        w="100%"
        justifyContent="space-between"
        px={{ base: 3, md: 5 }}
        py={4}
        borderBottom="1px solid #E5E7EB"
      >
        <VStack alignItems="flex-start" spacing={2}>
          <Link to="/campaigns/automations" style={{ textDecoration: "none" }}>
            <Button
              variant="link"
              color="#888888"
              fontWeight="500"
              fontSize="14px"
              leftIcon={<ArrowBackIcon />}
              height="auto"
              minW="auto"
            >
              Automations
            </Button>
          </Link>
          <Text
            fontSize="24px"
            fontWeight="600"
            lineHeight="100%"
            letterSpacing="-0.02em"
          >
            {automation.name}
          </Text>
        </VStack>

        <HStack alignItems="stretch">
          <Badge
            colorScheme={statusColorScheme(automation.status)}
            px={3}
            py={1}
            display="flex"
            alignItems="center"
            height="40px"
            fontSize="14px"
            borderRadius="md"
          >
            {automation.status}
          </Badge>
          <Button
            onClick={onToggleStatus}
            isLoading={isUpdatingStatus}
            bg="#FFFFFF"
            border="1px solid #E5E7EB"
            boxShadow="0px 6px 14px -6px rgba(0,0,0,0.15)"
            leftIcon={
              <Text display="flex" alignItems="center" justifyContent="center">
                <Icon as={automation.status === "ACTIVE" ? IoPause : IoPlay} />
              </Text>
            }
            height="40px"
            fontSize="14px"
            borderRadius="md"
          >
            {automation.status === "ACTIVE"
              ? "Pause Automation"
              : "Activate Automation"}
          </Button>
        </HStack>
      </HStack>

      <Stack
        direction={{ base: "column", lg: "row" }}
        w="100%"
        h="100%"
        gap={0}
      >
        <Box
          w={{ base: "100%", lg: "40%" }}
          p={6}
          borderRight={{ lg: "1px solid #E5E7EB" }}
        >
          <HStack justifyContent="space-between" alignItems="flex-start" mb={4}>
            <VStack alignItems="flex-start" spacing={1}>
              <Text
                fontSize="24px"
                fontWeight="600"
                lineHeight="100%"
                letterSpacing="-0.02em"
              >
                Summary
              </Text>
            </VStack>
            <Button
              variant="outline"
              borderColor="#E6E6E6"
              fontSize="14px"
              height="40px"
              onClick={onOpen}
              leftIcon={
                <Icon
                  as={PiFlowArrowLight}
                  boxSize={4}
                  aria-label="Flow Chart"
                  color="gray.800"
                  mr={1}
                />
              }
            >
              View Flow
            </Button>
          </HStack>

          <Text fontSize="18px" fontWeight="400" color="gray.800" mb={4}>
            {automation.description || "No summary provided."}
          </Text>

          <Divider my={6} />

          <Text
            fontSize="24px"
            fontWeight="600"
            lineHeight="100%"
            letterSpacing="-0.02em"
            mb={4}
          >
            Automation Results (Last 30 Days)
          </Text>

          <VStack alignItems="stretch" spacing={4}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              <StatCard title="Sent" value={formatNumber(last30?.sends)} />
              <StatCard
                title="% Opened"
                value={formatRate(last30?.open_rate)}
              />
              <StatCard
                title="% Clicked"
                value={formatRate(last30?.click_through_rate)}
              />
              <StatCard
                title="Tickets"
                value={formatNumber(last30?.tickets)}
                isKYD
                highlight
              />
              <StatCard
                title="Revenue"
                value={formatRevenue(last30?.revenue)}
                isKYD
                highlight
              />
              <StatCard
                title="Unsubscribes"
                value={formatNumber(last30?.unsubscribes)}
              />
            </Grid>

            <Divider />

            <Button
              variant="ghost"
              justifyContent="center"
              onClick={() => setShowOperationalMetrics((current) => !current)}
              rightIcon={
                showOperationalMetrics ? <ChevronUpIcon /> : <ChevronDownIcon />
              }
              color="gray.500"
              fontSize="xs"
              fontWeight="500"
              px={2}
              h="auto"
              py={1}
              _hover={{ bg: "gray.50" }}
              _active={{ bg: "gray.100" }}
            >
              Operational metrics
            </Button>

            {showOperationalMetrics && (
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <StatCard
                  title="Pending Runs"
                  value={formatNumber(runCounters?.pending)}
                />
                <StatCard
                  title="In Progress Runs"
                  value={formatNumber(runCounters?.in_progress)}
                />
                <StatCard
                  title="Completed Runs"
                  value={formatNumber(runCounters?.completed)}
                />
                <StatCard
                  title="Failed Runs"
                  value={formatNumber(runCounters?.failed)}
                />
              </Grid>
            )}
          </VStack>
        </Box>

        <Box
          w={{ base: "100%", lg: "60%" }}
          p={6}
          bg="#C9C9C9"
          backgroundImage={GRID_BG}
          backgroundSize="48px 48px"
          backgroundPosition="8px 24px"
          minH="100%"
          h="auto"
          top={{ lg: "0" }}
          alignSelf="flex-start"
        >
          <VStack
            alignItems="stretch"
            bg="#F5F5F5"
            border="1px solid #DDDDDD"
            borderRadius="24px"
            p={5}
            boxShadow="0px 10px 24px -12px rgba(0,0,0,0.3)"
          >
            {emailSteps.map((step, index) => {
              const metricsWithStepAttribution = automation.metrics as
                | MetricsWithStepAttribution
                | undefined;
              const stepMetrics =
                metricsWithStepAttribution?.attribution_by_step?.[step.id]
                  ?.last_30_days;
              const showStepDetails = showDetailsByStepId[step.id] ?? true;

              return (
                <Box key={step.id} px={6}>
                  <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    mb={4}
                  >
                    <Text fontSize="24px" fontWeight="600">
                      {titleFromStepId(step.id)}
                    </Text>
                  </HStack>
                  <Divider mb={4} />
                  <Box>
                    <Grid
                      templateColumns={{ base: "1fr", md: "120px 1fr" }}
                      rowGap={2}
                      columnGap={4}
                      mb={3}
                    >
                      <HStack alignItems="center" gap={2}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.3 9.60283C0.378793 9.66192 0.468454 9.70492 0.563864 9.72936C0.659274 9.7538 0.758564 9.75922 0.856066 9.74529C0.953568 9.73136 1.04737 9.69836 1.13212 9.64818C1.21687 9.598 1.29091 9.53162 1.35 9.45282C1.80409 8.84737 2.39291 8.35595 3.06983 8.01749C3.74675 7.67903 4.49318 7.50282 5.25 7.50282C6.00682 7.50282 6.75325 7.67903 7.43017 8.01749C8.10709 8.35595 8.69591 8.84737 9.15 9.45282C9.26949 9.61181 9.44723 9.71683 9.64413 9.74478C9.84104 9.77273 10.041 9.72133 10.2 9.60189C10.2567 9.55973 10.3069 9.50951 10.3491 9.45282C10.8032 8.84737 11.392 8.35595 12.0689 8.01749C12.7458 7.67903 13.4922 7.50282 14.2491 7.50282C15.0059 7.50282 15.7523 7.67903 16.4292 8.01749C17.1061 8.35595 17.695 8.84737 18.1491 9.45282C18.2685 9.61195 18.4463 9.71711 18.6433 9.74515C18.8403 9.77319 19.0404 9.72183 19.1995 9.60236C19.3587 9.48288 19.4638 9.30509 19.4919 9.10809C19.5199 8.91109 19.4685 8.71102 19.3491 8.55189C18.6852 7.66189 17.7999 6.96116 16.7812 6.51939C17.3397 6.00952 17.731 5.34272 17.9037 4.60651C18.0764 3.8703 18.0225 3.09907 17.7491 2.39403C17.4756 1.689 16.9954 1.0831 16.3715 0.655862C15.7476 0.228622 15.009 0 14.2528 0C13.4966 0 12.7581 0.228622 12.1341 0.655862C11.5102 1.0831 11.03 1.689 10.7566 2.39403C10.4831 3.09907 10.4292 3.8703 10.6019 4.60651C10.7747 5.34272 11.1659 6.00952 11.7244 6.51939C10.9889 6.83743 10.3207 7.29274 9.75562 7.86095C9.19057 7.29274 8.5224 6.83743 7.78687 6.51939C8.34533 6.00952 8.73659 5.34272 8.90931 4.60651C9.08203 3.8703 9.02814 3.09907 8.7547 2.39403C8.48126 1.689 8.00106 1.0831 7.37712 0.655862C6.75317 0.228622 6.01464 0 5.25844 0C4.50224 0 3.7637 0.228622 3.13976 0.655862C2.51581 1.0831 2.03561 1.689 1.76218 2.39403C1.48874 3.09907 1.43484 3.8703 1.60756 4.60651C1.78028 5.34272 2.17154 6.00952 2.73 6.51939C1.70686 6.95972 0.817219 7.6609 0.15 8.55282C0.0909052 8.63162 0.0479084 8.72128 0.0234647 8.81669C-0.00097899 8.9121 -0.00639086 9.01139 0.00753793 9.10889C0.0214667 9.20639 0.0544635 9.3002 0.104644 9.38494C0.154825 9.46969 0.221207 9.54373 0.3 9.60283ZM14.25 1.50282C14.695 1.50282 15.13 1.63478 15.5 1.88202C15.87 2.12925 16.1584 2.48065 16.3287 2.89179C16.499 3.30292 16.5436 3.75532 16.4568 4.19178C16.37 4.62823 16.1557 5.02915 15.841 5.34381C15.5263 5.65848 15.1254 5.87278 14.689 5.95959C14.2525 6.04641 13.8001 6.00185 13.389 5.83155C12.9778 5.66126 12.6264 5.37287 12.3792 5.00286C12.132 4.63285 12 4.19783 12 3.75282C12 3.15609 12.2371 2.58379 12.659 2.16183C13.081 1.73988 13.6533 1.50282 14.25 1.50282ZM5.25 1.50282C5.69501 1.50282 6.13002 1.63478 6.50003 1.88202C6.87004 2.12925 7.15843 2.48065 7.32873 2.89179C7.49903 3.30292 7.54358 3.75532 7.45677 4.19178C7.36995 4.62823 7.15566 5.02915 6.84099 5.34381C6.52632 5.65848 6.12541 5.87278 5.68895 5.95959C5.2525 6.04641 4.8001 6.00185 4.38896 5.83155C3.97783 5.66126 3.62643 5.37287 3.37919 5.00286C3.13196 4.63285 3 4.19783 3 3.75282C3 3.15609 3.23705 2.58379 3.65901 2.16183C4.08097 1.73988 4.65326 1.50282 5.25 1.50282ZM16.7812 16.2694C17.3397 15.7595 17.731 15.0927 17.9037 14.3565C18.0764 13.6203 18.0225 12.8491 17.7491 12.144C17.4756 11.439 16.9954 10.8331 16.3715 10.4059C15.7476 9.97862 15.009 9.75 14.2528 9.75C13.4966 9.75 12.7581 9.97862 12.1341 10.4059C11.5102 10.8331 11.03 11.439 10.7566 12.144C10.4831 12.8491 10.4292 13.6203 10.6019 14.3565C10.7747 15.0927 11.1659 15.7595 11.7244 16.2694C10.9889 16.5874 10.3207 17.0427 9.75562 17.611C9.19057 17.0427 8.5224 16.5874 7.78687 16.2694C8.34533 15.7595 8.73659 15.0927 8.90931 14.3565C9.08203 13.6203 9.02814 12.8491 8.7547 12.144C8.48126 11.439 8.00106 10.8331 7.37712 10.4059C6.75317 9.97862 6.01464 9.75 5.25844 9.75C4.50224 9.75 3.7637 9.97862 3.13976 10.4059C2.51581 10.8331 2.03561 11.439 1.76218 12.144C1.48874 12.8491 1.43484 13.6203 1.60756 14.3565C1.78028 15.0927 2.17154 15.7595 2.73 16.2694C1.70686 16.7097 0.817219 17.4109 0.15 18.3028C0.0909052 18.3816 0.0479084 18.4713 0.0234647 18.5667C-0.00097899 18.6621 -0.00639086 18.7614 0.00753793 18.8589C0.0214667 18.9564 0.0544635 19.0502 0.104644 19.1349C0.154825 19.2197 0.221207 19.2937 0.3 19.3528C0.378793 19.4119 0.468454 19.4549 0.563864 19.4794C0.659274 19.5038 0.758564 19.5092 0.856066 19.4953C0.953568 19.4814 1.04737 19.4484 1.13212 19.3982C1.21687 19.348 1.29091 19.2816 1.35 19.2028C1.80409 18.5974 2.39291 18.106 3.06983 17.7675C3.74675 17.429 4.49318 17.2528 5.25 17.2528C6.00682 17.2528 6.75325 17.429 7.43017 17.7675C8.10709 18.106 8.69591 18.5974 9.15 19.2028C9.26949 19.3618 9.44723 19.4668 9.64413 19.4948C9.84104 19.5227 10.041 19.4713 10.2 19.3519C10.2567 19.3097 10.3069 19.2595 10.3491 19.2028C10.8032 18.5974 11.392 18.106 12.0689 17.7675C12.7458 17.429 13.4922 17.2528 14.2491 17.2528C15.0059 17.2528 15.7523 17.429 16.4292 17.7675C17.1061 18.106 17.695 18.5974 18.1491 19.2028C18.2685 19.362 18.4463 19.4671 18.6433 19.4951C18.8403 19.5232 19.0404 19.4718 19.1995 19.3524C19.3587 19.2329 19.4638 19.0551 19.4919 18.8581C19.5199 18.6611 19.4685 18.461 19.3491 18.3019C18.6852 17.4119 17.7999 16.7112 16.7812 16.2694ZM5.25 11.2528C5.69501 11.2528 6.13002 11.3848 6.50003 11.632C6.87004 11.8793 7.15843 12.2307 7.32873 12.6418C7.49903 13.0529 7.54358 13.5053 7.45677 13.9418C7.36995 14.3782 7.15566 14.7791 6.84099 15.0938C6.52632 15.4085 6.12541 15.6228 5.68895 15.7096C5.2525 15.7964 4.8001 15.7519 4.38896 15.5816C3.97783 15.4113 3.62643 15.1229 3.37919 14.7529C3.13196 14.3828 3 13.9478 3 13.5028C3 12.9061 3.23705 12.3338 3.65901 11.9118C4.08097 11.4899 4.65326 11.2528 5.25 11.2528ZM14.25 11.2528C14.695 11.2528 15.13 11.3848 15.5 11.632C15.87 11.8793 16.1584 12.2307 16.3287 12.6418C16.499 13.0529 16.5436 13.5053 16.4568 13.9418C16.37 14.3782 16.1557 14.7791 15.841 15.0938C15.5263 15.4085 15.1254 15.6228 14.689 15.7096C14.2525 15.7964 13.8001 15.7519 13.389 15.5816C12.9778 15.4113 12.6264 15.1229 12.3792 14.7529C12.132 14.3828 12 13.9478 12 13.5028C12 12.9061 12.2371 12.3338 12.659 11.9118C13.081 11.4899 13.6533 11.2528 14.25 11.2528Z"
                            fill="#718096"
                          />
                        </svg>

                        <Text fontSize="14px" fontWeight="600" color="gray.600">
                          Audience
                        </Text>
                      </HStack>
                      <Text fontSize="14px" fontWeight="500" color="gray.600">
                        {deriveAudienceLabel(automation) ||
                          "People that match the automation conditions"}
                      </Text>
                    </Grid>
                    <Divider mb={3} />
                    {step.delay_minutes > 0 && (
                      <Grid
                        templateColumns={{ base: "1fr", md: "120px 1fr" }}
                        rowGap={2}
                        columnGap={4}
                        mb={3}
                      >
                        <HStack alignItems="center" gap={2}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.75 0C7.82164 0 5.93657 0.571828 4.33319 1.64317C2.72982 2.71451 1.48013 4.23726 0.742179 6.01884C0.00422452 7.80042 -0.188858 9.76082 0.187348 11.6521C0.563554 13.5434 1.49215 15.2807 2.85571 16.6443C4.21928 18.0079 5.95656 18.9365 7.84787 19.3127C9.73919 19.6889 11.6996 19.4958 13.4812 18.7578C15.2627 18.0199 16.7855 16.7702 17.8568 15.1668C18.9282 13.5634 19.5 11.6784 19.5 9.75C19.4973 7.16498 18.4692 4.68661 16.6413 2.85872C14.8134 1.03084 12.335 0.00272983 9.75 0ZM9.75 18C8.11831 18 6.52326 17.5161 5.16655 16.6096C3.80984 15.7031 2.75242 14.4146 2.128 12.9071C1.50358 11.3996 1.3402 9.74085 1.65853 8.1405C1.97685 6.54016 2.76259 5.07015 3.91637 3.91637C5.07016 2.76259 6.54017 1.97685 8.14051 1.65852C9.74085 1.34019 11.3997 1.50357 12.9071 2.12799C14.4146 2.75242 15.7031 3.80984 16.6096 5.16655C17.5161 6.52325 18 8.1183 18 9.75C17.9975 11.9373 17.1275 14.0343 15.5809 15.5809C14.0343 17.1275 11.9373 17.9975 9.75 18ZM15.75 9.75C15.75 9.94891 15.671 10.1397 15.5303 10.2803C15.3897 10.421 15.1989 10.5 15 10.5H9.75C9.55109 10.5 9.36033 10.421 9.21967 10.2803C9.07902 10.1397 9 9.94891 9 9.75V4.5C9 4.30109 9.07902 4.11032 9.21967 3.96967C9.36033 3.82902 9.55109 3.75 9.75 3.75C9.94892 3.75 10.1397 3.82902 10.2803 3.96967C10.421 4.11032 10.5 4.30109 10.5 4.5V9H15C15.1989 9 15.3897 9.07902 15.5303 9.21967C15.671 9.36032 15.75 9.55109 15.75 9.75Z"
                              fill="#718096"
                            />
                          </svg>
                          <Text
                            fontSize="14px"
                            fontWeight="600"
                            color="gray.600"
                          >
                            Sent
                          </Text>
                        </HStack>
                        <Text fontSize="14px" fontWeight="500" color="gray.600">
                          {formatDelayLabel(step.delay_minutes)}
                        </Text>
                      </Grid>
                    )}
                  </Box>

                  <Grid
                    templateColumns={{
                      base: "repeat(2, 1fr)",
                      md:
                        stepMetrics?.unsubscribes > 0
                          ? "repeat(5, 1fr)"
                          : "repeat(4, 1fr)",
                    }}
                    border="1px solid #DDDDDD"
                    borderRadius="20px"
                    bg="white"
                    textAlign="center"
                    overflow="hidden"
                    mb={5}
                  >
                    <Box p={4} borderRight="1px solid #DDDDDD">
                      <Text fontSize="14px" fontWeight="600" color="gray.500">
                        Sent
                      </Text>
                      <Text fontSize="24px" fontWeight="500" color="gray.600">
                        {formatNumber(stepMetrics?.sends)}
                      </Text>
                    </Box>
                    <Box p={4} borderRight="1px solid #E5E7EB">
                      <Text fontSize="14px" fontWeight="600" color="gray.500">
                        % Opened
                      </Text>
                      <Text fontSize="24px" fontWeight="500" color="gray.600">
                        {formatRate(stepMetrics?.open_rate)}
                      </Text>
                    </Box>
                    <Box p={4} borderRight="1px solid #E5E7EB">
                      <Text fontSize="14px" fontWeight="600" color="gray.500">
                        Tickets
                      </Text>
                      <Text fontSize="24px" fontWeight="500" color="gray.600">
                        {formatNumber(stepMetrics?.tickets)}
                      </Text>
                    </Box>
                    <Box p={4} borderRight="1px solid #E5E7EB">
                      <Text fontSize="14px" fontWeight="600" color="gray.500">
                        Revenue
                      </Text>
                      <Text fontSize="24px" fontWeight="500" color="gray.600">
                        {formatRevenue(stepMetrics?.revenue)}
                      </Text>
                    </Box>
                    {stepMetrics?.unsubscribes > 0 && (
                      <Box p={4}>
                        <Text fontSize="14px" fontWeight="600" color="gray.500">
                          Unsubscribes
                        </Text>
                        <Text fontSize="24px" fontWeight="500" color="gray.600">
                          {formatNumber(stepMetrics?.unsubscribes)}
                        </Text>
                      </Box>
                    )}
                  </Grid>
                  {showStepDetails && (
                    <Box
                      bgColor="white"
                      border="1px solid gray.600"
                      borderRadius="16px"
                      padding="24px"
                    >
                      <Text fontSize="24px" fontWeight="600" mb={3}>
                        Email
                      </Text>
                      <Divider mb={3} />
                      <Grid
                        templateColumns={{ base: "1fr", md: "120px 1fr" }}
                        rowGap={2}
                        columnGap={4}
                        mb={3}
                      >
                        <Text fontSize="14px" fontWeight="600" color="gray.600">
                          Subject
                        </Text>
                        <Text fontSize="14px" fontWeight="500" color="gray.600">
                          {automation.email_step_previews?.[step.id]
                            ?.subject_preview ||
                            "No subject preview available."}
                        </Text>
                      </Grid>
                      <Box
                        border="1px dashed #C4C4C4"
                        borderRadius="18px"
                        py={4}
                        px={8}
                        bg="#FAFAFA"
                      >
                        <Box
                          as="iframe"
                          title={`automation-email-preview-${step.id}`}
                          srcDoc={
                            automation.email_step_previews?.[step.id]
                              ?.html_string ||
                            "<html><body><p>No preview available.</p></body></html>"
                          }
                          sandbox="allow-same-origin"
                          w="100%"
                          h="860px"
                          border="0"
                          borderRadius="12px"
                          bg="white"
                        />
                      </Box>
                    </Box>
                  )}
                  <HStack justifyContent="center" mt={4}>
                    <Button
                      onClick={() =>
                        setShowDetailsByStepId((current) => ({
                          ...current,
                          [step.id]: !(current[step.id] ?? true),
                        }))
                      }
                      bg="white"
                      border="1px solid #D1D5DB"
                      borderRadius="full"
                      rightIcon={
                        showStepDetails ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      px={6}
                      fontWeight="500"
                      color="gray.600"
                    >
                      {showStepDetails ? "Hide Details" : "Show Details"}
                    </Button>
                  </HStack>

                  {index < emailSteps.length - 1 ? <Divider mt={6} /> : null}
                </Box>
              );
            })}
          </VStack>
        </Box>
      </Stack>

      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="24px" fontWeight="600">
            Automation Flow
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack alignItems="flex-start" spacing={4}>
              <AutomationsFlowGraph
                automation={automation}
                selectedStepId={selectedStepId}
                onSelectStep={setSelectedStepId}
                height="620px"
              />
              {selectedStep && (
                <Box
                  border="1px solid #E5E7EB"
                  borderRadius="12px"
                  p={4}
                  w="100%"
                >
                  <Text fontSize="24px" fontWeight="600" mb={2}>
                    Selected Step
                  </Text>
                  <HStack alignItems="center" gap={2}>
                    <Text fontSize="18px" fontWeight="500">
                      Name:
                    </Text>
                    <Text fontSize="16px" fontWeight="500" color="gray.600">
                      {selectedStep.name || selectedStep.id}
                    </Text>
                  </HStack>
                  {selectedStep.description && (
                    <HStack alignItems="center" gap={2}>
                      <Text fontSize="18px" fontWeight="500">
                        Description:
                      </Text>
                      <Text fontSize="16px" fontWeight="500" color="gray.600">
                        {selectedStep.description}
                      </Text>
                    </HStack>
                  )}
                  <HStack alignItems="center" gap={2}>
                    <Text fontSize="18px" fontWeight="500">
                      Type:
                    </Text>
                    {getStepTypeLabel(selectedStep)}
                  </HStack>
                  <HStack alignItems="center" gap={2}>
                    <Text fontSize="18px" fontWeight="500">
                      Details:
                    </Text>
                    {getStepAdditionalDetails(selectedStep)}
                  </HStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default AutomationsDetail;
